import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Tooltip from "./Tooltip";

/** Quantize drag values to avoid float noise; `step` is the smallest increment (e.g. 0.1, 0.01). */
function snapDragValueToStep(value, step) {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return value;
  const n = Math.round(value / step);
  const product = n * step;
  const str = String(step);
  const dot = str.indexOf(".");
  if (dot === -1) return product;
  const decPlaces = str.length - dot - 1;
  return Number(product.toFixed(decPlaces));
}

function decimalPlacesForStep(step) {
  const str = String(step);
  if (!str.includes(".")) return 0;
  return str.length - str.indexOf(".") - 1;
}

function clampNumber(value, min, max) {
  let next = value;
  if (Number.isFinite(min)) next = Math.max(min, next);
  if (Number.isFinite(max)) next = Math.min(max, next);
  return next;
}

/** Keep UA outline off the input; focused frame is rendered by overlay above stepper chrome. */
const MENU_NUMBER_INPUT_FIELD_CLASS = "outline-none";

/** Single subtle focus frame that sits above the up/down arrow layer. */
const MENU_NUMBER_INPUT_FOCUS_OVERLAY_CLASS =
  "pointer-events-none absolute inset-0 z-[2] rounded border border-transparent ring-0 transition-colors group-focus-within/number-input:border-gray-300 group-focus-within/number-input:ring-1 group-focus-within/number-input:ring-gray-200/25 group-focus-within/number-input:ring-offset-0";

export function formatCompactNumberInputValue(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;

  const fullText = String(value);
  if (fullText.length <= 9) return value;

  const absValue = Math.abs(numericValue);
  const roundedText =
    absValue >= 1000 || (absValue > 0 && absValue < 0.001)
      ? numericValue.toPrecision(4)
      : numericValue.toFixed(3);
  return String(Number(roundedText));
}

const DragNumberInput = ({
  label,
  value,
  dragValue,
  onChange,
  onFocus,
  onBlur,
  onDragValueChange,
  onDragStart,
  onDragEnd,
  dragSensitivity,
  /** If set, drag snaps to this increment; otherwise matches `inputProps.step` (same as arrow buttons). */
  dragSnapStep,
  /** When true, drag previews locally and commits once on pointer-up. */
  deferDragUpdates = true,
  /** When set, the value shown in the input while dragging is formatted with this many fraction digits. */
  dragDisplayDecimals = null,
  disabled = false,
  inputProps = {},
  wrapperClassName = "space-y-1",
  labelClassName = "text-xs text-gray-300",
  /** Classes for the wrapper around the input + steppers (e.g. `min-w-0 flex-1` for label-left rows). */
  inputWrapperClassName = "",
  inputClassName = "",
  displayValueFormatter = null,
  tooltipContent = null,
  showStepper = true,
  stepperPlacement = "overlay",
}) => {
  const inputRef = useRef(null);
  const dragStateRef = useRef(null);
  const dragHandlersRef = useRef({ move: null, up: null });
  const dragRafRef = useRef(0);
  const pendingDragValueRef = useRef(null);
  const dragPreviewValueRef = useRef(null);
  const [dragPreviewValue, setDragPreviewValue] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);

  // Keep callback refs stable so stopDrag/startDrag don't need them as deps
  const onDragStartRef = useRef(onDragStart);
  const onDragEndRef = useRef(onDragEnd);
  const onDragValueChangeRef = useRef(onDragValueChange);
  useEffect(() => {
    onDragStartRef.current = onDragStart;
  }, [onDragStart]);
  useEffect(() => {
    onDragEndRef.current = onDragEnd;
  }, [onDragEnd]);
  useEffect(() => {
    onDragValueChangeRef.current = onDragValueChange;
  }, [onDragValueChange]);

  const emitDragValueNow = useCallback(() => {
    if (pendingDragValueRef.current == null) return;
    const next = pendingDragValueRef.current;
    pendingDragValueRef.current = null;
    if (typeof onDragValueChangeRef.current === "function") {
      onDragValueChangeRef.current(next);
    }
  }, []);

  const scheduleDragValueEmit = useCallback(
    (nextValue) => {
      pendingDragValueRef.current = nextValue;
      if (dragRafRef.current) return;
      dragRafRef.current = window.requestAnimationFrame(() => {
        dragRafRef.current = 0;
        emitDragValueNow();
      });
    },
    [emitDragValueNow]
  );

  const stopDrag = useCallback(() => {
    const { move, up } = dragHandlersRef.current;
    if (move) window.removeEventListener("pointermove", move);
    if (up) window.removeEventListener("pointerup", up);
    dragHandlersRef.current = { move: null, up: null };
    if (dragRafRef.current) {
      window.cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = 0;
    }
    if (deferDragUpdates) {
      if (
        dragPreviewValueRef.current != null &&
        typeof onDragValueChangeRef.current === "function"
      ) {
        onDragValueChangeRef.current(dragPreviewValueRef.current);
      }
    } else {
      emitDragValueNow();
    }
    pendingDragValueRef.current = null;
    dragPreviewValueRef.current = null;
    setDragPreviewValue(null);
    if (dragStateRef.current) {
      dragStateRef.current = null;
      if (typeof onDragEndRef.current === "function") onDragEndRef.current();
    }
  }, [deferDragUpdates, emitDragValueNow]);

  useEffect(() => () => stopDrag(), [stopDrag]);

  const dragEnabled = !disabled && typeof onDragValueChange === "function";
  const stepAmount =
    Number.isFinite(Number(inputProps.step)) && Number(inputProps.step) > 0
      ? Number(inputProps.step)
      : 1;
  const effectiveSensitivity = Number.isFinite(dragSensitivity) ? dragSensitivity : stepAmount;
  /** Match arrow stepping: snap label-drag to `inputProps.step` unless `dragSnapStep` overrides. */
  const effectiveDragSnapStep =
    Number.isFinite(dragSnapStep) && dragSnapStep > 0 ? dragSnapStep : stepAmount;
  const minValue = Number(inputProps.min);
  const maxValue = Number(inputProps.max);

  const startDrag = useCallback(
    (e) => {
      if (!dragEnabled) return;
      if (e.button !== 0) return;
      e.preventDefault();
      stopDrag();

      const parsedValue = Number.isFinite(dragValue) ? dragValue : Number(value);
      const startValue = Number.isFinite(parsedValue) ? parsedValue : 0;
      dragStateRef.current = { startClientX: e.clientX, startValue };
      if (deferDragUpdates) {
        dragPreviewValueRef.current = startValue;
        setDragPreviewValue(startValue);
      }
      if (typeof onDragStartRef.current === "function") onDragStartRef.current();

      const move = (ev) => {
        if (!dragStateRef.current) return;
        const delta = ev.clientX - dragStateRef.current.startClientX;
        let nextValue = dragStateRef.current.startValue + delta * effectiveSensitivity;
        if (Number.isFinite(effectiveDragSnapStep) && effectiveDragSnapStep > 0) {
          nextValue = snapDragValueToStep(nextValue, effectiveDragSnapStep);
        }
        nextValue = clampNumber(nextValue, minValue, maxValue);
        if (deferDragUpdates) {
          dragPreviewValueRef.current = nextValue;
          setDragPreviewValue(nextValue);
        } else {
          scheduleDragValueEmit(nextValue);
        }
      };

      const up = () => {
        stopDrag();
      };

      dragHandlersRef.current = { move, up };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [
      dragEnabled,
      dragValue,
      value,
      effectiveSensitivity,
      effectiveDragSnapStep,
      minValue,
      maxValue,
      deferDragUpdates,
      scheduleDragValueEmit,
      stopDrag,
    ]
  );

  const combinedLabelClassName = `${labelClassName}${
    dragEnabled ? " select-none cursor-ew-resize" : ""
  }${disabled ? " cursor-not-allowed" : ""}`;
  const {
    className: inputPropsClassName,
    onChange: _inputOnChange,
    onFocus: _inputOnFocus,
    onBlur: _inputOnBlur,
    value: _inputValue,
    type: _inputType,
    disabled: _inputDisabled,
    style: inputPropsStyle,
    ...restInputProps
  } = inputProps;
  const rawDisplayValue =
    dragPreviewValue != null
      ? Number.isFinite(dragDisplayDecimals) && dragDisplayDecimals >= 0
        ? Number(dragPreviewValue).toFixed(Math.floor(dragDisplayDecimals))
        : dragPreviewValue
      : value;

  const displayValue =
    !inputFocused && dragPreviewValue == null && typeof displayValueFormatter === "function"
      ? displayValueFormatter(value)
      : rawDisplayValue;

  const handleFocus = useCallback(
    (event) => {
      setInputFocused(true);
      if (typeof onFocus === "function") onFocus(event);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (event) => {
      setInputFocused(false);
      if (typeof onBlur === "function") onBlur(event);
    },
    [onBlur]
  );

  const readOnly = restInputProps.readOnly === true;
  const canStep = showStepper && !disabled && !inputProps.disabled && !readOnly;
  const stepDecimals = decimalPlacesForStep(stepAmount);
  const inlineStepper = canStep && stepperPlacement === "inline";
  const combinedInputClassName = [
    "menu-number-input",
    inputClassName,
    inputPropsClassName,
    MENU_NUMBER_INPUT_FIELD_CLASS,
    inlineStepper ? "min-w-0 flex-1 basis-0 rounded-r-none" : "",
    disabled ? "cursor-not-allowed" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const inputStyle =
    inlineStepper && inputPropsStyle && typeof inputPropsStyle === "object"
      ? { ...inputPropsStyle, minWidth: 0, paddingRight: "0.5rem" }
      : inlineStepper
        ? { minWidth: 0, paddingRight: "0.5rem" }
        : inputPropsStyle;
  const emitStepChange = useCallback(
    (direction) => {
      if (!canStep || typeof onChange !== "function") return;
      const currentRaw = inputFocused
        ? (inputRef.current?.value ?? rawDisplayValue)
        : rawDisplayValue;
      const current = Number(currentRaw);
      const base = Number.isFinite(current) ? current : 0;
      const stepped = clampNumber(
        snapDragValueToStep(base + direction * stepAmount, stepAmount),
        minValue,
        maxValue
      );
      const nextValue = stepDecimals > 0 ? stepped.toFixed(stepDecimals) : String(stepped);
      onChange({
        target: {
          value: nextValue,
          name: restInputProps.name,
        },
        currentTarget: {
          value: nextValue,
          name: restInputProps.name,
        },
      });
      inputRef.current?.focus({ preventScroll: true });
    },
    [
      canStep,
      inputFocused,
      maxValue,
      minValue,
      onChange,
      rawDisplayValue,
      restInputProps.name,
      stepAmount,
      stepDecimals,
    ]
  );

  const handleLabelPointerDown = useCallback(
    (event) => {
      event.stopPropagation();
      startDrag(event);
    },
    [startDrag]
  );

  const inputUi = (
    <div className={`${wrapperClassName}${disabled ? " cursor-not-allowed" : ""}`}>
      <label
        className={combinedLabelClassName}
        data-no-panel-drag="true"
        onPointerDown={handleLabelPointerDown}
      >
        {label}
      </label>
      <div
        className={[
          "group/number-input relative",
          inlineStepper ? "flex" : "",
          inputWrapperClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <input
          ref={inputRef}
          type="number"
          value={displayValue}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={combinedInputClassName}
          style={inputStyle}
          disabled={disabled || inputProps.disabled}
          {...restInputProps}
        />
        {canStep ? (
          <div
            className={
              inlineStepper
                ? "drag-number-input-stepper relative z-[1] -ml-px w-5 shrink-0 flex-col overflow-hidden rounded-r border border-gray-600 border-l-gray-700 opacity-100"
                : "drag-number-input-stepper pointer-events-none absolute bottom-px right-px top-px z-[1] w-5 flex-col overflow-hidden rounded-r border-l border-gray-700 opacity-0 transition-opacity group-hover/number-input:opacity-100 group-focus-within/number-input:opacity-100"
            }
          >
            <button
              type="button"
              tabIndex={-1}
              className="pointer-events-auto flex min-h-0 flex-1 items-center justify-center bg-gray-700/80 text-gray-300 hover:bg-gray-600 hover:text-gray-100"
              aria-label={`Increase ${label}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => emitStepChange(1)}
            >
              <ChevronUp className="h-3 w-3" aria-hidden="true" />
            </button>
            <button
              type="button"
              tabIndex={-1}
              className="pointer-events-auto flex min-h-0 flex-1 items-center justify-center border-t border-gray-800 bg-gray-700/80 text-gray-300 hover:bg-gray-600 hover:text-gray-100"
              aria-label={`Decrease ${label}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => emitStepChange(-1)}
            >
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            </button>
          </div>
        ) : null}
        <span className={MENU_NUMBER_INPUT_FOCUS_OVERLAY_CLASS} aria-hidden="true" />
      </div>
    </div>
  );

  if (tooltipContent) {
    return (
      <Tooltip content={tooltipContent} fullWidth>
        {inputUi}
      </Tooltip>
    );
  }

  return inputUi;
};

export default DragNumberInput;
